import { useState, useEffect } from 'react';
import {
    Check, CreditCard, ArrowRight, Loader2, Users, Building, Globe,
    Download, FileText, Calendar, ChevronRight, AlertCircle, Shield,
    Receipt, Settings, ExternalLink, CircleDollarSign, Sparkles
} from 'lucide-react';
import { billingService } from '../lib/services';
import { useAuth } from '../contexts/AuthContext';
import type { Subscription } from '../types/api';

/* ─────────── Plan Data ─────────── */

const INDIVIDUAL_PLANS = [
    {
        id: 'TRIAL',
        name: 'Controlled Free Trial',
        shortName: 'Trial',
        priceMonthly: 0,
        priceYearly: 0,
        features: [
            'Delegate Replica capabilities',
            'Limited daily messages (50/day)',
            'Limited tool executions (20/day)',
            'Guardian visible (approvals disabled)',
        ],
        color: 'from-slate-500 to-slate-600',
        isTrial: true,
    },
    {
        id: 'PERSONAL_SHADOW',
        name: 'Personal Shadow',
        shortName: 'Shadow',
        priceMonthly: 19,
        priceYearly: 190,
        features: [
            'Memory & context retention',
            'Advice & drafting assistance',
            'Read-only tool access',
            'Full audit logs',
        ],
        color: 'from-slate-500 to-slate-600',
    },
    {
        id: 'DELEGATE_REPLICA',
        name: 'Delegate Replica',
        shortName: 'Delegate',
        priceMonthly: 49,
        priceYearly: 490,
        features: [
            'Autonomous task execution',
            'Tool usage via MCP',
            'Guardian approval workflows',
            'Identity-bound actions',
            'Up to 3 replicas',
        ],
        popular: true,
        color: 'from-sky-500 to-blue-600',
    },
    {
        id: 'FULL_REPLICA',
        name: 'Full Replica',
        shortName: 'Full',
        priceMonthly: 99,
        priceYearly: 990,
        features: [
            'Public professional profile',
            'Hireable by third parties',
            'Revenue sharing enabled',
            'Up to 5 replicas',
        ],
        color: 'from-purple-500 to-pink-600',
        requiresApplication: true,
    },
];

const ENTERPRISE_PLANS = [
    {
        id: 'TEAM_STARTER',
        name: 'Team Starter',
        shortName: 'Team',
        priceMonthly: 199,
        priceYearly: 2190,
        commission: '20%',
        features: [
            'Up to 5 team members',
            'Hire up to 5 replicas',
            '1 workspace',
            'Email support',
        ],
        color: 'from-slate-500 to-slate-600',
    },
    {
        id: 'BUSINESS',
        name: 'Business',
        shortName: 'Business',
        priceMonthly: 499,
        priceYearly: 5490,
        commission: '15%',
        features: [
            'Up to 25 team members',
            'Hire up to 20 replicas',
            'Up to 5 workspaces',
            'SSO (SAML/OIDC)',
            'Priority support',
        ],
        popular: true,
        color: 'from-sky-500 to-blue-600',
    },
    {
        id: 'ENTERPRISE_CUSTOM',
        name: 'Enterprise',
        shortName: 'Enterprise',
        priceMonthly: 0,
        priceYearly: 0,
        commission: '10%',
        isCustom: true,
        features: [
            'Unlimited everything',
            'Dedicated account manager',
            'On-premise deployment',
            '24/7 dedicated support',
        ],
        color: 'from-amber-500 to-orange-600',
    },
];

/* ─────────── Mock Data ─────────── */

const MOCK_INVOICES = [
    { id: 'INV-2026-0019', date: '2026-02-01', amount: 49.00, status: 'paid' as const, plan: 'Delegate Replica' },
    { id: 'INV-2026-0018', date: '2026-01-01', amount: 49.00, status: 'paid' as const, plan: 'Delegate Replica' },
    { id: 'INV-2025-0017', date: '2025-12-01', amount: 49.00, status: 'paid' as const, plan: 'Delegate Replica' },
    { id: 'INV-2025-0016', date: '2025-11-01', amount: 19.00, status: 'paid' as const, plan: 'Personal Shadow' },
    { id: 'INV-2025-0015', date: '2025-10-01', amount: 19.00, status: 'paid' as const, plan: 'Personal Shadow' },
];

/* ─────────── Sub-Views ─────────── */

type BillingTab = 'overview' | 'plans' | 'invoices' | 'payment';

interface BillingViewProps {
    onNavigate?: (view: string) => void;
    hideHeader?: boolean;
}

export default function BillingView({ onNavigate: _onNavigate, hideHeader }: BillingViewProps) {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<BillingTab>('overview');
    const [planCategory, setPlanCategory] = useState<'individual' | 'enterprise'>('individual');
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);

    // ── Real billing data from API ─────────────────────────────────────
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [billingLoading, setBillingLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await billingService.getSubscription();
                if (!cancelled && res?.data) {
                    setSubscription(res.data as unknown as Subscription);
                }
            } catch {
                // Keep defaults on error
            } finally {
                if (!cancelled) setBillingLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Resolve current plan from subscription (fallback to Trial)
    const resolvedPlanId = subscription?.planType || 'TRIAL';
    const currentPlan = INDIVIDUAL_PLANS.find(p => p.id === resolvedPlanId) || INDIVIDUAL_PLANS[0];
    const currentBillingPeriod = subscription ? (subscription.cancelAtPeriodEnd ? 'canceled' : 'monthly') : 'monthly';
    const nextBillingDate = subscription?.currentPeriodEnd
        ? new Date(subscription.currentPeriodEnd).toISOString().slice(0, 10)
        : '—';
    const activePlans = planCategory === 'individual' ? INDIVIDUAL_PLANS : ENTERPRISE_PLANS;

    const handleUpgrade = async (planId: string) => {
        setSelectedPlan(planId);
        setIsCheckingOut(true);

        if (planId === 'TRIAL') {
            alert('Trial activated!');
            setIsCheckingOut(false);
            setSelectedPlan(null);
            return;
        }
        if (planId === 'ENTERPRISE_CUSTOM') {
            alert('Our team will contact you at your registered email. sales@humanreplica.ai');
            setIsCheckingOut(false);
            setSelectedPlan(null);
            return;
        }

        const response = await billingService.createCheckout({
            planType: planId as 'TRIAL' | 'PERSONAL_SHADOW' | 'DELEGATE_REPLICA' | 'FULL_REPLICA',
            billingPeriod,
            successUrl: window.location.origin + '/payment-success',
            cancelUrl: window.location.origin + '/billing',
        });

        if (response.data?.url) {
            window.location.href = response.data.url;
        } else {
            alert('Checkout would redirect to Stripe in production!');
            setIsCheckingOut(false);
            setSelectedPlan(null);
        }
    };

    const tabs: { id: BillingTab; label: string; icon: React.ReactNode }[] = [
        { id: 'overview', label: 'Overview', icon: <CircleDollarSign size={16} /> },
        { id: 'plans', label: 'Change Plan', icon: <Sparkles size={16} /> },
        { id: 'invoices', label: 'Invoices', icon: <Receipt size={16} /> },
        { id: 'payment', label: 'Payment Method', icon: <CreditCard size={16} /> },
    ];

    return (
        <div className="max-w-5xl mx-auto space-y-6">

            {/* Page Header */}
            {!hideHeader && (
                <div>
                    <h1 className="text-2xl font-bold text-hig-text-primary">Billing & Subscription</h1>
                    <p className="text-sm text-hig-text-tertiary mt-1">Manage your plan, payment methods, and invoices</p>
                </div>
            )}

            {/* Tab Navigation */}
            <div className="flex gap-1 bg-hig-bg-tertiary rounded-xl p-1 border border-hig-border w-fit">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === tab.id
                                ? 'bg-hig-bg-elevated text-hig-text-primary shadow-sm'
                                : 'text-hig-text-tertiary hover:text-hig-text-secondary'
                            }`}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ═══════════ OVERVIEW TAB ═══════════ */}
            {activeTab === 'overview' && (
                billingLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 size={24} className="animate-spin text-hig-text-tertiary" />
                    </div>
                ) : (
                <div className="space-y-6">

                    {/* Current Plan Card */}
                    <div className="card p-6">
                        <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                                    <Shield size={22} className="text-white" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h2 className="text-xl font-bold text-hig-text-primary">{currentPlan.name}</h2>
                                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                                            Active
                                        </span>
                                    </div>
                                    <p className="text-sm text-hig-text-tertiary mt-1">Your replica can act for you</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-3xl font-bold text-hig-text-primary">€{currentPlan.priceMonthly}</p>
                                <p className="text-xs text-hig-text-tertiary">per month</p>
                            </div>
                        </div>

                        <div className="h-px bg-hig-divider my-5" />

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bg-hig-bg-tertiary rounded-lg p-4">
                                <div className="flex items-center gap-2 text-hig-text-tertiary mb-1">
                                    <Calendar size={14} />
                                    <span className="text-xs font-medium uppercase tracking-wider">Billing Period</span>
                                </div>
                                <p className="text-sm font-semibold text-hig-text-primary capitalize">{currentBillingPeriod}</p>
                            </div>
                            <div className="bg-hig-bg-tertiary rounded-lg p-4">
                                <div className="flex items-center gap-2 text-hig-text-tertiary mb-1">
                                    <Calendar size={14} />
                                    <span className="text-xs font-medium uppercase tracking-wider">Next Invoice</span>
                                </div>
                                <p className="text-sm font-semibold text-hig-text-primary">{nextBillingDate}</p>
                            </div>
                            <div className="bg-hig-bg-tertiary rounded-lg p-4">
                                <div className="flex items-center gap-2 text-hig-text-tertiary mb-1">
                                    <CreditCard size={14} />
                                    <span className="text-xs font-medium uppercase tracking-wider">Payment</span>
                                </div>
                                <p className="text-sm font-semibold text-hig-text-primary">•••• 4242</p>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-5">
                            <button
                                onClick={() => setActiveTab('plans')}
                                className="btn-primary text-sm px-5 py-2.5 rounded-xl flex items-center gap-2"
                            >
                                <Sparkles size={14} />
                                Upgrade Plan
                            </button>
                            <button
                                onClick={() => setShowCancelModal(true)}
                                className="text-sm px-5 py-2.5 rounded-xl border border-hig-border text-hig-text-secondary hover:text-red-400 hover:border-red-500/30 transition-colors"
                            >
                                Cancel Subscription
                            </button>
                        </div>
                    </div>

                    {/* Usage Summary */}
                    <div className="card p-6">
                        <h3 className="text-lg font-semibold text-hig-text-primary mb-4">Current Usage</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-hig-text-secondary">Active Replicas</span>
                                    <span className="text-sm font-semibold text-hig-text-primary">2 / 3</span>
                                </div>
                                <div className="w-full bg-hig-bg-tertiary rounded-full h-2">
                                    <div className="bg-sky-500 h-2 rounded-full transition-all" style={{ width: '66%' }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-hig-text-secondary">Messages Today</span>
                                    <span className="text-sm font-semibold text-hig-text-primary">127 / ∞</span>
                                </div>
                                <div className="w-full bg-hig-bg-tertiary rounded-full h-2">
                                    <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: '25%' }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-hig-text-secondary">Tool Executions</span>
                                    <span className="text-sm font-semibold text-hig-text-primary">43 / ∞</span>
                                </div>
                                <div className="w-full bg-hig-bg-tertiary rounded-full h-2">
                                    <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: '15%' }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Invoices Preview */}
                    <div className="card p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-hig-text-primary">Recent Invoices</h3>
                            <button
                                onClick={() => setActiveTab('invoices')}
                                className="text-sm text-hig-accent hover:text-hig-accent/80 transition-colors flex items-center gap-1"
                            >
                                View All <ChevronRight size={14} />
                            </button>
                        </div>
                        <div className="space-y-2">
                            {MOCK_INVOICES.slice(0, 3).map((inv) => (
                                <div key={inv.id} className="flex items-center justify-between py-3 px-4 rounded-lg bg-hig-bg-tertiary hover:bg-hig-bg-elevated transition-colors">
                                    <div className="flex items-center gap-3">
                                        <FileText size={16} className="text-hig-text-tertiary" />
                                        <div>
                                            <p className="text-sm font-medium text-hig-text-primary">{inv.id}</p>
                                            <p className="text-xs text-hig-text-tertiary">{inv.date} · {inv.plan}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-semibold text-hig-text-primary">€{inv.amount.toFixed(2)}</span>
                                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400">
                                            {inv.status}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                )
            )}

            {/* ═══════════ CHANGE PLAN TAB ═══════════ */}
            {activeTab === 'plans' && (
                <div className="space-y-6">

                    {/* Individual / Enterprise Toggle */}
                    <div className="flex items-center justify-center">
                        <div className="inline-flex items-center bg-hig-bg-tertiary rounded-xl p-1 border border-hig-border">
                            <button
                                onClick={() => setPlanCategory('individual')}
                                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${planCategory === 'individual'
                                        ? 'bg-hig-bg-elevated text-hig-text-primary shadow-sm'
                                        : 'text-hig-text-tertiary hover:text-hig-text-secondary'
                                    }`}
                            >
                                <span className="flex items-center gap-2">
                                    <Users size={16} />
                                    Individual
                                </span>
                            </button>
                            <button
                                onClick={() => setPlanCategory('enterprise')}
                                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${planCategory === 'enterprise'
                                        ? 'bg-hig-bg-elevated text-hig-text-primary shadow-sm'
                                        : 'text-hig-text-tertiary hover:text-hig-text-secondary'
                                    }`}
                            >
                                <span className="flex items-center gap-2">
                                    <Building size={16} />
                                    Enterprise
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Billing Period Toggle */}
                    <div className="flex items-center justify-center gap-4">
                        <button
                            onClick={() => setBillingPeriod('monthly')}
                            className={`px-4 py-2 rounded-lg text-sm transition-colors ${billingPeriod === 'monthly'
                                ? 'bg-hig-bg-elevated text-hig-text-primary'
                                : 'text-hig-text-tertiary hover:text-hig-text-primary'
                                }`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setBillingPeriod('yearly')}
                            className={`px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${billingPeriod === 'yearly'
                                ? 'bg-hig-bg-elevated text-hig-text-primary'
                                : 'text-hig-text-tertiary hover:text-hig-text-primary'
                                }`}
                        >
                            Yearly
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Save ~8%</span>
                        </button>
                    </div>

                    {/* Plan Cards */}
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${planCategory === 'individual' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
                        }`}>
                        {activePlans.map((plan) => {
                            const isCustom = 'isCustom' in plan && plan.isCustom;
                            const isCurrent = plan.id === currentPlan.id;
                            const price = isCustom ? 0 : (billingPeriod === 'monthly' ? plan.priceMonthly : plan.priceYearly);
                            const monthlyEquivalent = isCustom ? 0 : (billingPeriod === 'yearly' ? Math.round(price / 12) : price);
                            const commission = 'commission' in plan ? plan.commission : null;

                            return (
                                <div
                                    key={plan.id}
                                    className={`relative card overflow-hidden transition-all duration-200 hover:-translate-y-1 ${plan.popular ? 'ring-2 ring-hig-accent' : ''
                                        } ${isCurrent ? 'ring-2 ring-emerald-500' : ''}`}
                                >
                                    {plan.popular && !isCurrent && (
                                        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-sky-500 to-blue-600 text-white text-xs text-center py-1 font-medium">
                                            {planCategory === 'individual' ? 'Core Plan' : 'Most Popular'}
                                        </div>
                                    )}
                                    {isCurrent && (
                                        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs text-center py-1 font-medium">
                                            Current Plan
                                        </div>
                                    )}

                                    <div className={`p-5 ${(plan.popular || isCurrent) ? 'pt-9' : ''}`}>
                                        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center mb-3`}>
                                            <CreditCard size={16} className="text-white" />
                                        </div>

                                        <h3 className="text-lg font-bold text-hig-text-primary">{plan.name}</h3>

                                        {/* Price — centered */}
                                        <div className="mt-4 text-center">
                                            {isCustom ? (
                                                <span className="text-2xl font-bold text-hig-text-primary">Custom</span>
                                            ) : (
                                                <>
                                                    <div className="flex items-baseline justify-center gap-1">
                                                        <span className="text-3xl font-bold text-hig-text-primary">€{monthlyEquivalent}</span>
                                                        <span className="text-hig-text-tertiary text-sm">/mo</span>
                                                    </div>
                                                    {billingPeriod === 'yearly' && price > 0 && (
                                                        <p className="text-xs text-hig-text-tertiary mt-0.5">€{price}/yr</p>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {commission && (
                                            <div className="mt-3 flex justify-center">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-hig-accent/10 text-hig-accent text-xs font-medium">
                                                    <Globe size={10} />
                                                    {commission} commission
                                                </span>
                                            </div>
                                        )}

                                        <ul className="mt-4 space-y-2">
                                            {plan.features.map((f) => (
                                                <li key={f} className="flex items-start gap-2">
                                                    <Check size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                                                    <span className="text-xs text-hig-text-secondary">{f}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <button
                                            onClick={() => !isCurrent && handleUpgrade(plan.id)}
                                            disabled={(isCheckingOut && selectedPlan === plan.id) || isCurrent}
                                            className={`w-full mt-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${isCurrent
                                                    ? 'bg-emerald-500/10 text-emerald-400 cursor-default border border-emerald-500/20'
                                                    : plan.popular
                                                        ? 'btn-primary'
                                                        : 'bg-hig-bg-tertiary text-hig-text-primary hover:bg-hig-bg-elevated'
                                                } disabled:opacity-50`}
                                        >
                                            {isCurrent ? (
                                                <>
                                                    <Check size={14} />
                                                    Current Plan
                                                </>
                                            ) : isCheckingOut && selectedPlan === plan.id ? (
                                                <>
                                                    <Loader2 size={14} className="animate-spin" />
                                                    Processing…
                                                </>
                                            ) : (
                                                <>
                                                    {isCustom ? 'Contact Sales' : plan.priceMonthly > currentPlan.priceMonthly ? 'Upgrade' : 'Switch'}
                                                    <ArrowRight size={14} />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {planCategory === 'enterprise' && (
                        <p className="text-center text-xs text-hig-text-tertiary italic">
                            + replica hiring costs based on expertise & experience
                        </p>
                    )}
                </div>
            )}

            {/* ═══════════ INVOICES TAB ═══════════ */}
            {activeTab === 'invoices' && (
                <div className="space-y-6">
                    <div className="card overflow-hidden">
                        {/* Invoices Header */}
                        <div className="p-5 border-b border-hig-divider flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-hig-text-primary">Invoice History</h3>
                                <p className="text-xs text-hig-text-tertiary mt-0.5">Download invoices for your records and tax purposes</p>
                            </div>
                            <button className="text-sm px-4 py-2 rounded-lg border border-hig-border text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-elevated transition-colors flex items-center gap-2">
                                <Download size={14} />
                                Export All (CSV)
                            </button>
                        </div>

                        {/* Invoice Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-hig-divider">
                                        <th className="text-left px-5 py-3 text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Invoice</th>
                                        <th className="text-left px-5 py-3 text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Date</th>
                                        <th className="text-left px-5 py-3 text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Plan</th>
                                        <th className="text-left px-5 py-3 text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Amount</th>
                                        <th className="text-left px-5 py-3 text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Status</th>
                                        <th className="text-right px-5 py-3 text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {MOCK_INVOICES.map((inv) => (
                                        <tr key={inv.id} className="border-b border-hig-divider/50 hover:bg-hig-bg-tertiary transition-colors">
                                            <td className="px-5 py-4">
                                                <span className="text-sm font-medium text-hig-text-primary">{inv.id}</span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className="text-sm text-hig-text-secondary">{inv.date}</span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className="text-sm text-hig-text-secondary">{inv.plan}</span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className="text-sm font-semibold text-hig-text-primary">€{inv.amount.toFixed(2)}</span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${inv.status === 'paid'
                                                        ? 'bg-emerald-500/15 text-emerald-400'
                                                        : inv.status === 'pending'
                                                            ? 'bg-amber-500/15 text-amber-400'
                                                            : 'bg-red-500/15 text-red-400'
                                                    }`}>
                                                    {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button className="p-1.5 rounded-lg hover:bg-hig-bg-elevated transition-colors text-hig-text-tertiary hover:text-hig-text-primary" title="Download PDF">
                                                        <Download size={14} />
                                                    </button>
                                                    <button className="p-1.5 rounded-lg hover:bg-hig-bg-elevated transition-colors text-hig-text-tertiary hover:text-hig-text-primary" title="View Invoice">
                                                        <ExternalLink size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Billing Info Footer */}
                        <div className="p-5 bg-hig-bg-tertiary border-t border-hig-divider">
                            <div className="flex items-start gap-3">
                                <AlertCircle size={16} className="text-hig-text-tertiary mt-0.5 flex-shrink-0" />
                                <div className="text-xs text-hig-text-tertiary">
                                    <p>Invoices are generated on the 1st of each month. All amounts include applicable taxes.</p>
                                    <p className="mt-1">Need a custom invoice or W-8BEN? Contact <a href="mailto:billing@humanreplica.ai" className="text-hig-accent hover:underline">billing@humanreplica.ai</a></p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Billing Address */}
                    <div className="card p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-hig-text-primary">Billing Information</h3>
                            <button className="text-sm text-hig-accent hover:text-hig-accent/80 transition-colors flex items-center gap-1">
                                <Settings size={14} />
                                Edit
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <p className="text-xs font-medium text-hig-text-tertiary uppercase tracking-wider mb-2">Billing Name</p>
                                <p className="text-sm text-hig-text-primary">{user?.displayName || user?.email || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-hig-text-tertiary uppercase tracking-wider mb-2">Email</p>
                                <p className="text-sm text-hig-text-primary">{user?.email || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-hig-text-tertiary uppercase tracking-wider mb-2">Address</p>
                                <p className="text-sm text-hig-text-secondary italic">Not provided</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-hig-text-tertiary uppercase tracking-wider mb-2">Tax ID / VAT</p>
                                <p className="text-sm text-hig-text-secondary italic">Not provided</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════ PAYMENT METHOD TAB ═══════════ */}
            {activeTab === 'payment' && (
                <div className="space-y-6">
                    {/* Current Payment Method */}
                    <div className="card p-6">
                        <h3 className="text-lg font-semibold text-hig-text-primary mb-4">Payment Method</h3>

                        <div className="bg-hig-bg-tertiary rounded-xl p-5 border border-hig-border flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-700 flex items-center justify-center">
                                    <span className="text-white text-xs font-bold tracking-wider">VISA</span>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-hig-text-primary">Visa ending in 4242</p>
                                    <p className="text-xs text-hig-text-tertiary">Expires 12/2028</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                                    Default
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-5">
                            <button className="text-sm px-5 py-2.5 rounded-xl border border-hig-border text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-elevated transition-colors flex items-center gap-2">
                                <CreditCard size={14} />
                                Update Card
                            </button>
                            <button className="text-sm px-5 py-2.5 rounded-xl border border-hig-border text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-elevated transition-colors flex items-center gap-2">
                                <CreditCard size={14} />
                                Add New Method
                            </button>
                        </div>
                    </div>

                    {/* Payment Security */}
                    <div className="card p-6">
                        <h3 className="text-lg font-semibold text-hig-text-primary mb-4">Payment Security</h3>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 text-sm text-hig-text-secondary">
                                <div className="w-8 h-8 rounded-lg bg-hig-bg-tertiary flex items-center justify-center">
                                    <Shield size={16} className="text-emerald-400" />
                                </div>
                                <div>
                                    <p className="font-medium text-hig-text-primary">PCI DSS Level 1 Compliant</p>
                                    <p className="text-xs text-hig-text-tertiary">All payments processed by Stripe. We never store your full card number.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-hig-text-secondary">
                                <div className="w-8 h-8 rounded-lg bg-hig-bg-tertiary flex items-center justify-center">
                                    <Shield size={16} className="text-sky-400" />
                                </div>
                                <div>
                                    <p className="font-medium text-hig-text-primary">3D Secure Enabled</p>
                                    <p className="text-xs text-hig-text-tertiary">Extra authentication for online payments where supported.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-hig-text-secondary">
                                <div className="w-8 h-8 rounded-lg bg-hig-bg-tertiary flex items-center justify-center">
                                    <Shield size={16} className="text-purple-400" />
                                </div>
                                <div>
                                    <p className="font-medium text-hig-text-primary">SEPA Direct Debit Available</p>
                                    <p className="text-xs text-hig-text-tertiary">European bank transfer option available for annual plans.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Accepted Methods */}
                    <div className="card p-6">
                        <h3 className="text-lg font-semibold text-hig-text-primary mb-3">Accepted Payment Methods</h3>
                        <div className="flex flex-wrap gap-3">
                            {['Visa', 'Mastercard', 'Amex', 'SEPA', 'iDEAL', 'Bancontact'].map((method) => (
                                <div key={method} className="px-4 py-2 rounded-lg bg-hig-bg-tertiary border border-hig-border text-xs font-medium text-hig-text-secondary">
                                    {method}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════ CANCEL MODAL ═══════════ */}
            {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="card p-6 max-w-md w-full mx-4">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center flex-shrink-0">
                                <AlertCircle size={20} className="text-red-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-hig-text-primary">Cancel Subscription?</h3>
                                <p className="text-sm text-hig-text-secondary mt-1">
                                    Your plan will remain active until the end of your current billing period ({nextBillingDate}).
                                    After that, your replicas will be paused and you'll lose access to paid features.
                                </p>
                            </div>
                        </div>

                        <div className="bg-red-500/5 border border-red-500/15 rounded-lg p-4 mb-5">
                            <p className="text-sm text-hig-text-secondary">
                                <strong className="text-hig-text-primary">What happens next:</strong>
                            </p>
                            <ul className="mt-2 space-y-1 text-sm text-hig-text-secondary">
                                <li>• Active replicas will be suspended</li>
                                <li>• Autonomous task execution will stop</li>
                                <li>• Data is retained for 90 days</li>
                                <li>• You can reactivate anytime</li>
                            </ul>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowCancelModal(false)}
                                className="text-sm px-5 py-2.5 rounded-xl border border-hig-border text-hig-text-secondary hover:text-hig-text-primary transition-colors"
                            >
                                Keep My Plan
                            </button>
                            <button
                                onClick={() => {
                                    alert('Subscription cancellation scheduled. Active until ' + nextBillingDate);
                                    setShowCancelModal(false);
                                }}
                                className="text-sm px-5 py-2.5 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors"
                            >
                                Cancel Subscription
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
